"use client";

import { useState, useEffect, memo, useMemo, useRef, useCallback } from "react";
import styles from "./PostFeed.module.css";
import {
  commentsAPI,
  likesAPI,
  sharesAPI,
  postsAPI,
  socialUtils,
  handleApiError,
} from "../../../services/api";
import MultiMediaDisplay from "./MultiMediaDisplay";
import AdoptionPostEmbed from "./AdoptionPostEmbed";
import ChannelPostReactions from "../../channels/ChannelPostReactions";
import ChannelPostComments from "../../channels/ChannelPostComments";
import PostReactionsModal from "./PostReactionsModal";
import ReportModal from "../../modals/ReportModal";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../hooks/useAuth";
import { getUserPlaceholderImage } from "../../../utils/imageUtils";
import imageCacheService from "../../../services/ImageCacheService";
import { showSuccessToast, showErrorToast } from "../../../utils/notificationUtils";
import { usePostModal } from "../../../contexts/PostModalContext";
import "../../channels/ChannelPost.css";
import "../../channels/ChannelPostReactions.css";
import "../../channels/ChannelPostComments.css";

const PostFeed = ({
  posts,
  currentUser,
  onUpdatePost,
  onDeletePost,
  onLikeToggle,
  onCommentAdded,
  onLoadMore,
  hasMorePosts,
  isLoading,
  onEditPost,
  onNewRepost,
}) => {
  const { getUserType, getProfileImageUrl } = useAuth();
  const { openPostModal } = usePostModal();

  // Get user type color
  const currentUserType = getUserType();
  const getUserTypeColor = (userType) => {
    const userTypeConfig = {
      'PET_GUARDIAN': '#8548A8', // Purple
      'VETERINARIAN': '#00D4B3', // Cyan
      'SERVICE_PROVIDER': '#D65745', // Orange
      'SERVICE_PROVIDER': '#D65745', // Orange
      'CLINIC_MODERATOR': '#2A8AD2', // Blue
    };
    return userTypeConfig[userType] || '#8548A8'; // Default to purple
  };
  const userTypeColor = getUserTypeColor(currentUserType);

  const normalizeId = (id) => {
    if (id === null || id === undefined) return null;
    return String(id);
  };

  const currentUserId = useMemo(() => {
    return normalizeId(currentUser?.id ?? currentUser?.user_id ?? currentUser?.userId);
  }, [currentUser?.id, currentUser?.user_id, currentUser?.userId]);

  const [expandedPosts, setExpandedPosts] = useState(new Set());
  const [commentInputs, setCommentInputs] = useState({});
  const [commentsData, setCommentsData] = useState({});
  const [loadingComments, setLoadingComments] = useState({});
  const [submittingComments, setSubmittingComments] = useState({});
  const [likingPosts, setLikingPosts] = useState({});
  const [showDropdowns, setShowDropdowns] = useState({});
  const [expandedComments, setExpandedComments] = useState({});
  const { t } = useTranslation();

  // Reaction state
  const [reactionsData, setReactionsData] = useState({});
  const [userReactions, setUserReactions] = useState({});
  const [reactingPosts, setReactingPosts] = useState({});
  // Removed showReactionPicker - now handled by ChannelPostReactions component


  // Reactions modal state
  const [showReactionsModal, setShowReactionsModal] = useState(false);
  const [selectedPostId, setSelectedPostId] = useState(null);

  // Poll state
  const [selectedPollOptions, setSelectedPollOptions] = useState({});
  const [isSubmittingPollVote, setIsSubmittingPollVote] = useState({});
  const [userPollVotes, setUserPollVotes] = useState({});

  // Report modal state
  const [showReportModal, setShowReportModal] = useState(false);
  const [selectedPostForReport, setSelectedPostForReport] = useState(null);

  // Ref to track loading comments to prevent duplicate calls
  const loadingCommentsRef = useRef(new Set());

  // Load comments for a post
  const loadComments = async (postId) => {

    // Enhanced protection against duplicate calls using ref
    if (loadingCommentsRef.current.has(postId)) {
      return;
    }

    // Check if comments are already loaded
    if (commentsData[postId]) {
      return;
    }


    // Add to loading ref
    loadingCommentsRef.current.add(postId);
    setLoadingComments((prev) => ({ ...prev, [postId]: true }));

    try {
      const response = await commentsAPI.getPostComments(postId, {
        page: 1,
        perPage: 10,
      });

      const transformedComments = response.data.data.map(
        socialUtils.transformCommentData
      );

      setCommentsData((prev) => ({
        ...prev,
        [postId]: {
          comments: transformedComments,
          hasMore: response.data.current_page < response.data.last_page,
          currentPage: response.data.current_page,
        },
      }));
    } catch (error) {
      console.error("Error loading comments:", error);
    } finally {
      // Remove from loading ref
      loadingCommentsRef.current.delete(postId);
      setLoadingComments((prev) => ({ ...prev, [postId]: false }));
    }
  };

  // Format timestamp
  const formatTimestamp = socialUtils.formatTimestamp;

  // Toggle post expansion
  const togglePostExpansion = (postId) => {
    const newExpanded = new Set(expandedPosts);
    if (newExpanded.has(postId)) {
      newExpanded.delete(postId);
    } else {
      newExpanded.add(postId);
    }
    setExpandedPosts(newExpanded);
  };

  // Handle comment input changes
  const handleCommentChange = (postId, value) => {
    setCommentInputs((prev) => ({ ...prev, [postId]: value }));
  };

  // Handle comment updates (for reply counts, etc.)
  const handleCommentUpdate = (postId, commentId, updates) => {

    // Handle immediate update from CommentReactions
    if (updates.immediateUpdate) {

      setCommentsData((prev) => {
        const updatedComments = prev[postId]?.comments.map((comment) =>
          comment.id === commentId
            ? {
              ...comment,
              user_reaction_type: updates.user_reaction_type,
              user_reaction_count: updates.user_reaction_count,
              user_reaction_emoji: updates.user_reaction_emoji,
              user_reaction_id: updates.user_reaction_id,
              is_liked: updates.is_liked,
              reaction_counts: updates.reaction_counts,
              total_reactions: updates.total_reactions,
              has_user_reaction: updates.has_user_reaction,
            }
            : comment
        );


        return {
          ...prev,
          [postId]: {
            ...prev[postId],
            comments: updatedComments,
          },
        };
      });

      // Also refresh from server after a short delay to ensure consistency
      setTimeout(() => {
        loadComments(postId);
      }, 2000);

      return;
    }

    // Handle refresh request from CommentReactions
    if (updates.refresh) {
      loadComments(postId);
      return;
    }

    // Handle regular updates (reply counts, etc.)
    setCommentsData((prev) => ({
      ...prev,
      [postId]: {
        ...prev[postId],
        comments: prev[postId]?.comments.map((comment) =>
          comment.id === commentId ? { ...comment, ...updates } : comment
        ),
      },
    }));
  };

  // Submit comment
  const handleCommentSubmit = async (postId) => {
    const comment = commentInputs[postId];
    if (!comment?.trim() || submittingComments[postId]) return;


    setSubmittingComments((prev) => ({ ...prev, [postId]: true }));

    try {
      const response = await commentsAPI.createComment(postId, {
        text: comment.trim(),
      });

      const newComment = socialUtils.transformCommentData({
        ...response.data,
        user: {
          id: currentUser.id,
          first_name:
            currentUser.first_name || currentUser.name?.split(" ")[0] || "",
          last_name:
            currentUser.last_name ||
            currentUser.name?.split(" ").slice(1).join(" ") ||
            "",
          username: currentUser.username,
          profile_image_url:
            currentUser.profile_image_url || currentUser.avatar,
        },
        created_at: new Date().toISOString(),
      });

      // Add comment to local state
      setCommentsData((prev) => ({
        ...prev,
        [postId]: {
          ...prev[postId],
          comments: [newComment, ...(prev[postId]?.comments || [])],
        },
      }));

      // Notify parent component about the new comment
      if (onCommentAdded) {
        onCommentAdded(postId);
      }

      // Clear input
      setCommentInputs((prev) => ({ ...prev, [postId]: "" }));
    } catch (error) {
      console.error("Error submitting comment:", error);
    } finally {
      setSubmittingComments((prev) => ({ ...prev, [postId]: false }));
    }
  };

  // Handle like toggle
  const handleLikeToggle = async (postId) => {
    if (likingPosts[postId]) return;

    setLikingPosts((prev) => ({ ...prev, [postId]: true }));

    try {
      // Call the parent component's like toggle handler
      if (onLikeToggle) {
        await onLikeToggle(postId);
      }
    } catch (error) {
      console.error("Error toggling like:", error);
      // Error handling is done in the parent component
    } finally {
      setLikingPosts((prev) => ({ ...prev, [postId]: false }));
    }
  };

  // Handle share
  const handleShare = async (postId) => {
    try {
      // Get share URL from post object first (if available from consolidated-feed)
      const currentPost = posts.find(p => p.id === postId);
      let shareUrl = currentPost?.share_url || null;

      const response = await sharesAPI.sharePost(postId);
      const responseData = response?.data?.data || response?.data || {};

      // Get share URL from backend response (preferred) or use existing from post, or fallback to constructing it
      shareUrl = responseData?.share_url || responseData?.url || shareUrl ||
        `${window.location.origin}/social/profile/posts/${postId}`;

      // Copy URL to clipboard
      const didCopy = await copyTextToClipboard(shareUrl);
      if (didCopy) {
        showSuccessToast(t("postFeed.shareActions.copySuccess") || "Link copied to clipboard!");
      } else {
        showErrorToast(t("postFeed.shareActions.copyError") || "Failed to copy link");
        window.prompt(t("postFeed.shareActions.manualCopyPrompt") || "Copy this link:", shareUrl);
      }

      // Update share count from backend response
      if (responseData?.shares_count !== undefined && stableOnUpdatePost && currentPost) {
        const updatedPost = {
          ...currentPost,
          share_url: shareUrl, // Update share_url in post object
          reactions: {
            ...currentPost.reactions,
            shares: responseData.shares_count,
          },
        };
        stableOnUpdatePost(postId, updatedPost);
      }
    } catch (error) {
      console.error("Error sharing post:", error);
      // Even if API fails, try to copy a fallback URL or use existing share_url
      const currentPost = posts.find(p => p.id === postId);
      const fallbackUrl = currentPost?.share_url || `${window.location.origin}/social/profile/posts/${postId}`;
      try {
        const didCopy = await copyTextToClipboard(fallbackUrl);
        if (didCopy) {
          showSuccessToast(t("postFeed.shareActions.copySuccess") || "Link copied to clipboard!");
        }
      } catch (copyErr) {
        showErrorToast(t("postFeed.shareActions.copyError") || "Failed to copy link");
      }
    }
  };

  const copyTextToClipboard = async (text) => {
    try {
      if (navigator?.clipboard?.writeText && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        return true;
      }
    } catch (err) {
      // fall through
    }

    try {
      const textarea = document.createElement("textarea");
      textarea.value = text;
      textarea.setAttribute("readonly", "");
      textarea.style.position = "fixed";
      textarea.style.top = "-9999px";
      textarea.style.left = "-9999px";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      const successful = document.execCommand("copy");
      document.body.removeChild(textarea);
      return successful;
    } catch (err) {
      return false;
    }
  };

  // Handle reaction toggle
  const handleReactionToggle = async (postId, reactionType) => {
    if (reactingPosts[postId]) return;

    setReactingPosts((prev) => ({ ...prev, [postId]: true }));

    try {
      const currentPost = posts.find((p) => p.id === postId);
      const currentReaction =
        currentPost?.user_reaction_type || userReactions[postId];

      if (currentReaction === reactionType) {
        // Remove reaction
        await likesAPI.removeReaction(postId);
        setUserReactions((prev) => ({ ...prev, [postId]: null }));

        // IMMEDIATE UPDATE: Update the post data to remove reaction
        const updatedPost = posts.find((p) => p.id === postId);
        if (updatedPost) {
          const newPost = {
            ...updatedPost,
            user_reaction_type: null,
            reactions_count: Math.max(
              0,
              (updatedPost.reactions_count || 0) - 1
            ),
            reaction_counts: updatedPost.reaction_counts
              ? {
                ...updatedPost.reaction_counts,
                [reactionType]: Math.max(
                  0,
                  (updatedPost.reaction_counts[reactionType] || 0) - 1
                ),
              }
              : {},
            // Handle legacy reactions
            reactions: updatedPost.reactions
              ? {
                ...updatedPost.reactions,
                likes: Math.max(0, (updatedPost.reactions.likes || 0) - 1),
              }
              : updatedPost.reactions,
          };

          // Call onUpdatePost to update the parent component
          if (onUpdatePost) {
            await onUpdatePost(postId, newPost);
          }
        }

        // Update local reaction data
        setReactionsData((prev) => {
          const postData = prev[postId];
          if (!postData) return prev;

          const newReactions = postData.reactions.filter(
            (r) => r.reaction_type !== reactionType
          );
          const newStats = {
            ...postData.stats,
            total: Math.max(0, postData.stats.total - 1),
            by_type: {
              ...postData.stats.by_type,
              [reactionType]: Math.max(
                0,
                (postData.stats.by_type[reactionType] || 0) - 1
              ),
            },
          };

          return {
            ...prev,
            [postId]: {
              ...postData,
              reactions: newReactions,
              stats: newStats,
            },
          };
        });
      } else {
        // Add/change reaction
        await likesAPI.addReaction(postId, reactionType);
        setUserReactions((prev) => ({ ...prev, [postId]: reactionType }));

        // IMMEDIATE UPDATE: Update the post data to add reaction
        const updatedPost = posts.find((p) => p.id === postId);
        if (updatedPost) {
          const newPost = {
            ...updatedPost,
            user_reaction_type: reactionType,
            reactions_count:
              (updatedPost.reactions_count || 0) + (currentReaction ? 0 : 1), // Only increment if no previous reaction
            reaction_counts: updatedPost.reaction_counts
              ? {
                ...updatedPost.reaction_counts,
                [reactionType]:
                  (updatedPost.reaction_counts[reactionType] || 0) + 1,
                ...(currentReaction && {
                  [currentReaction]: Math.max(
                    0,
                    (updatedPost.reaction_counts[currentReaction] || 0) - 1
                  ),
                }),
              }
              : { [reactionType]: 1 },
            // Handle legacy reactions
            reactions: updatedPost.reactions
              ? {
                ...updatedPost.reactions,
                likes:
                  (updatedPost.reactions.likes || 0) +
                  (currentReaction ? 0 : 1),
              }
              : updatedPost.reactions,
          };

          // Call onUpdatePost to update the parent component
          if (onUpdatePost) {
            await onUpdatePost(postId, newPost);
          }
        }

        // Update local reaction data
        setReactionsData((prev) => {
          const postData = prev[postId];
          if (!postData) return prev;

          // Remove previous reaction if exists
          let newReactions = postData.reactions.filter(
            (r) => r.reaction_type !== currentReaction
          );

          // Add new reaction
          const existingReaction = newReactions.find(
            (r) => r.reaction_type === reactionType
          );
          if (existingReaction) {
            existingReaction.count += 1;
          } else {
            newReactions.push({
              reaction_type: reactionType,
              count: 1,
              user_id: currentUser?.id,
            });
          }

          const newStats = {
            ...postData.stats,
            total: postData.stats.total + (currentReaction ? 0 : 1), // Only increment if no previous reaction
            by_type: {
              ...postData.stats.by_type,
              [reactionType]: (postData.stats.by_type[reactionType] || 0) + 1,
              ...(currentReaction && {
                [currentReaction]: Math.max(
                  0,
                  (postData.stats.by_type[currentReaction] || 0) - 1
                ),
              }),
            },
          };

          return {
            ...prev,
            [postId]: {
              ...postData,
              reactions: newReactions,
              stats: newStats,
            },
          };
        });
      }
    } catch (error) {
      console.error("Error toggling reaction:", error);
    } finally {
      setReactingPosts((prev) => ({ ...prev, [postId]: false }));
      // Removed - reaction picker now handled by ChannelPostReactions
    }
  };

  // Removed toggleReactionPicker - now handled by ChannelPostReactions component

  // Custom emoji image paths
  const HappyEmoji = "/icons/Reactions/Sulhafa_Emoji_Happy.png";
  const LoveEmoji = "/icons/Reactions/Sulhafa_Emoji_Love.png";
  const LaughEmoji = "/icons/Reactions/Sulhafa_Emoji_Laugh.png";
  const ShockedEmoji = "/icons/Reactions/Sulhafa_Emoji_Shocked.png";
  const SadEmoji = "/icons/Reactions/Sulhafa_Emoji_Sad.png";
  const AngryEmoji = "/icons/Reactions/Sulhafa_Emoji_Angry.png";

  // Available reaction types
  const reactionTypes = useMemo(
    () => [
      { type: "like", emoji: HappyEmoji, label: t("postFeed.reactions.like") },
      { type: "love", emoji: LoveEmoji, label: t("postFeed.reactions.love") },
      { type: "haha", emoji: LaughEmoji, label: t("postFeed.reactions.haha") },
      { type: "wow", emoji: ShockedEmoji, label: t("postFeed.reactions.wow") },
      { type: "sad", emoji: SadEmoji, label: t("postFeed.reactions.sad") },
      { type: "angry", emoji: AngryEmoji, label: t("postFeed.reactions.angry") },
    ],
    [t]
  );

  // Initialize reactions data from posts API response using useMemo for better performance
  const processedReactionsData = useMemo(() => {
    if (posts && posts.length > 0) {
      const newReactionsData = {};
      const newUserReactions = {};

      posts.forEach((post, index) => {
        // Process reaction data from API response
        const reactionCounts =
          post.reaction_counts &&
            typeof post.reaction_counts === "object" &&
            !Array.isArray(post.reaction_counts)
            ? post.reaction_counts
            : {};
        const userReactionType = post.user_reaction_type || null;
        const totalReactions = post.reactions_count || 0;

        // Fallback for legacy reaction data
        const legacyReactions = post.reactions || {};
        const hasLegacyData = legacyReactions.likes !== undefined;
        // Handle both new and legacy reaction data
        let finalReactionCounts = reactionCounts;
        let finalTotalReactions = totalReactions;
        let finalUserReaction = userReactionType;

        // If no new reaction data but legacy data exists, use legacy data
        if (Object.keys(reactionCounts).length === 0 && hasLegacyData) {
          finalReactionCounts = { like: legacyReactions.likes || 0 };
          finalTotalReactions = legacyReactions.likes || 0;
          finalUserReaction = post.is_liked ? "like" : null;
        }

        // Convert reaction_counts object to array format for consistency
        const reactions = Object.entries(finalReactionCounts).map(
          ([type, count]) => ({
            reaction_type: type,
            count: count,
            user_id: finalUserReaction === type ? currentUser?.id : null,
          })
        );

        newReactionsData[post.id] = {
          reactions,
          stats: {
            total: finalTotalReactions,
            by_type: finalReactionCounts,
            user_reaction: finalUserReaction,
          },
          hasMore: false,
          currentPage: 1,
        };

        if (finalUserReaction) {
          newUserReactions[post.id] = finalUserReaction;
        }
      });
      return {
        reactionsData: newReactionsData,
        userReactions: newUserReactions,
      };
    }
    return { reactionsData: {}, userReactions: {} };
  }, [posts, currentUser?.id]);

  // Update state when processed data changes
  useEffect(() => {
    setReactionsData(processedReactionsData.reactionsData);
    setUserReactions(processedReactionsData.userReactions);
  }, [processedReactionsData]);

  // Preload images when posts change
  useEffect(() => {
    if (posts && posts.length > 0) {
      const imageUrls = []

      posts.forEach(post => {
        // Collect profile images
        if (post.author?.avatar) {
          imageUrls.push(post.author.avatar)
        }
        if (post.channel?.profileImage) {
          imageUrls.push(post.channel.profileImage)
        }

        // Collect media images
        if (post.media && Array.isArray(post.media)) {
          post.media.forEach(item => {
            const url = typeof item === 'string' ? item : item.url
            const type = typeof item === 'string' ?
              (item.includes('.mp4') || item.includes('.mov') || item.includes('.webm') || item.includes('.m3u8') ? 'video' : 'image') :
              item.file_type

            // Preload images and video thumbnails
            if (type === 'image') {
              imageUrls.push(url)
            } else if (type === 'video' && item.thumbnail) {
              imageUrls.push(item.thumbnail)
            }
          })
        }
      })

      // Preload all collected images
      if (imageUrls.length > 0) {
        imageCacheService.preloadImages(imageUrls)
      }
    }
  }, [posts])

  // Removed reaction picker click outside handler - now handled by ChannelPostReactions component

  // Toggle dropdown
  const toggleDropdown = (postId) => {
    setShowDropdowns((prev) => ({
      ...prev,
      [postId]: !prev[postId],
    }));
  };

  // Start editing a post - opens modal
  const startEditPost = (post) => {
    if (onEditPost) {
      onEditPost(post);
    }
    setShowDropdowns({}); // Close dropdowns
  };

  // Cancel editing a post

  // Close all dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Only close dropdowns if clicking outside dropdown elements
      const dropdownElement = event.target.closest("[data-dropdown]");
      if (!dropdownElement && Object.keys(showDropdowns).length > 0) {
        setShowDropdowns({});
      }
    };

    // Use passive listener for better performance
    document.addEventListener("click", handleClickOutside, { passive: true });
    return () => document.removeEventListener("click", handleClickOutside);
  }, [showDropdowns]);

  // Toggle comments expansion
  const toggleComments = (postId) => {
    // Check if comments are already being loaded to prevent duplicate calls
    if (loadingComments[postId]) {
      return;
    }

    setExpandedComments((prev) => ({
      ...prev,
      [postId]: !prev[postId],
    }));

    // Load comments if not already loaded
    if (!commentsData[postId] && !loadingComments[postId]) {
      loadComments(postId);
    }
  };

  const getActionIcon = (type, size = 16) => {
    const iconColor = "#494949";

    switch (type) {
      case "like":
        return (
          <svg width={size} height={size} viewBox="0 0 24 24" fill={iconColor}>
            <path d="M2 20h2c.55 0 1-.45 1-1v-9c0-.55-.45-1-1-1H2v11zm19.83-7.12c.11-.25.17-.52.17-.8 0-.83-.67-1.5-1.5-1.5H16l.86-4.27c.08-.4.14-.84.14-1.28 0-.65-.13-1.26-.36-1.82-.97-2.31-4.04-2.31-5.01 0-.23.56-.36 1.17-.36 1.82 0 .44.06.88.14 1.28L12 13H9v7h11c.83 0 1.54-.5 1.85-1.22l2.96-7.9z" />
          </svg>
        );
      case "like-filled":
        return (
          <svg width={size} height={size} viewBox="0 0 24 24" fill="#8548A8">
            <path d="M2 20h2c.55 0 1-.45 1-1v-9c0-.55-.45-1-1-1H2v11zm19.83-7.12c.11-.25.17-.52.17-.8 0-.83-.67-1.5-1.5-1.5H16l.86-4.27c.08-.4.14-.84.14-1.28 0-.65-.13-1.26-.36-1.82-.97-2.31-4.04-2.31-5.01 0-.23.56-.36 1.17-.36 1.82 0 .44.06.88.14 1.28L12 13H9v7h11c.83 0 1.54-.5 1.85-1.22l2.96-7.9z" />
          </svg>
        );
      case "comment":
        return (
          <svg width={size} height={size} viewBox="0 0 24 24" fill={iconColor}>
            <path d="M21.99 4c0-1.1-.89-2-2-2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h14l4 4-.01-18zM18 14H6v-2h12v2zm0-3H6V9h12v2zm0-3H6V6h12v2z" />
          </svg>
        );
      case "repost":
        return (
          <svg width={size} height={size} viewBox="0 0 24 24" fill={iconColor}>
            <path d="M19 7v4H5.83l3.58-3.59L8 6l-6 6 6 6 1.41-1.41L5.83 13H21V7z" />
          </svg>
        );
      case "share":
        return (
          <svg width={size} height={size} viewBox="0 0 24 24" fill={iconColor}>
            <path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3c-.79 0-1.5.31-2.04.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92 1.61 0 2.92-1.31 2.92-2.92s-1.31-2.92-2.92-2.92z" />
          </svg>
        );
      case "gift":
        return (
          <svg width={size} height={size} viewBox="0 0 24 24" fill={iconColor}>
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1.5 17.5v-9L7 14l1.5-7.5H12v11zm1.5 0V6.5h3.5L19 14l-3.5-3.5v9z" />
          </svg>
        );
      case "edit":
        return (
          <svg width={size} height={size} viewBox="0 0 24 24" fill={iconColor}>
            <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" />
          </svg>
        );
      case "delete":
        return (
          <svg width={size} height={size} viewBox="0 0 24 24" fill={iconColor}>
            <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
          </svg>
        );
      default:
        return null;
    }
  };

  // Function to detect if text contains Arabic characters
  const isArabicText = (text) => {
    if (!text) return false;
    // Arabic Unicode range: U+0600-U+06FF, U+0750-U+077F, U+08A0-U+08FF, U+FB50-U+FDFF, U+FE70-U+FEFF
    const arabicRegex =
      /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/;
    return arabicRegex.test(text);
  };

  const formatContent = (content) => {
    if (!content) return "";

    const isArabic = isArabicText(content);

    // Robustly linkify URLs
    const urlRegex = /(<a\b[^>]*>[\s\S]*?<\/a>)|(<[^>]+>)|(https?:\/\/[^\s<]+)/gi;
    const linkifiedContent = content.replace(urlRegex, (match, aTag, otherTag, url) => {
      if (aTag) return aTag;
      if (otherTag) return otherTag;
      if (url) return `<a href="${url}" target="_blank" rel="noopener noreferrer" style="color: #00D4B3; text-decoration: underline;" onclick="event.stopPropagation()">${url}</a>`;
      return match;
    });

    // Check if originally the content had HTML tags
    const hasHTML = /<[^>]+>/.test(content);

    if (hasHTML) {
      return (
        <div
          style={{
            direction: isArabic ? "rtl" : "ltr",
            textAlign: isArabic ? "right" : "left",
            lineHeight: "1.5",
          }}
          dangerouslySetInnerHTML={{ __html: linkifiedContent }}
        />
      );
    }

    return linkifiedContent.split("\n").map((line, index) => {
      if (line.trim().startsWith("•")) {
        return (
          <div
            key={index}
            style={{
              marginLeft: isArabic ? "0" : "16px",
              marginRight: isArabic ? "16px" : "0",
              marginBottom: "4px",
              direction: isArabic ? "rtl" : "ltr",
              textAlign: isArabic ? "right" : "left",
            }}
          >
            <span dangerouslySetInnerHTML={{ __html: line.trim() }} />
          </div>
        );
      }
      return (
        <div
          key={index}
          style={{
            direction: isArabic ? "rtl" : "ltr",
            textAlign: isArabic ? "right" : "left",
            marginBottom: line.trim() ? "8px" : "4px",
          }}
        >
          <span dangerouslySetInnerHTML={{ __html: line || "\u00A0" }} />
        </div>
      );
    });
  };

  const truncateContent = (content, maxLength = 200) => {
    if (!content || content.length <= maxLength) return content;
    return content.substring(0, maxLength) + "...";
  };

  // Memoize onUpdatePost to prevent unnecessary re-renders
  const stableOnUpdatePost = useCallback((postId, updates) => {
    if (onUpdatePost) {
      onUpdatePost(postId, updates);
    }
  }, [onUpdatePost]);

  // Handle showing reactions modal
  const handleShowReactions = useCallback((postId) => {
    setSelectedPostId(postId);
    setShowReactionsModal(true);
  }, []);

  // Handle closing reactions modal
  const handleCloseReactionsModal = useCallback(() => {
    setShowReactionsModal(false);
    setSelectedPostId(null);
  }, []);

  return (
    <div className={styles["post-feed-container"]} >
      {posts && Array.isArray(posts) && posts.map((post) => {
        // Skip posts that reference a deleted adoption post
        // If adoption_post_id is set but adoption_post data is missing, the adoption post was deleted
        if (post.adoption_post_id && !post.adoption_post) return null;

        const isExpanded = expandedPosts.has(post.id);
        const shouldShowExpansion =
          post.hasMoreContent && post.content && post.content.length > 200;
        const displayContent =
          shouldShowExpansion && !isExpanded
            ? truncateContent(post.content, 200)
            : post.content;
        const postComments = commentsData[post.id]?.comments || [];
        const showComments = expandedComments[post.id];

        const isChannelPost = !!post.channel;
        const author = post.author || {};
        const authorId = normalizeId(
          author?.id ??
          author?.user_id ??
          author?.userId ??
          post?.user_id ??
          post?.userId
        );
        const isOwner = !!(currentUserId && authorId && currentUserId === authorId);
        const primaryAvatar = isChannelPost
          ? post.channel?.profileImage || author?.avatar || getUserPlaceholderImage(author)
          : author?.avatar || getUserPlaceholderImage(author);
        const primaryName = isChannelPost
          ? post.channel?.name || author?.username
          : author?.username || "Unknown User";

        // Post type detection (similar to ChannelPost)
        const normalizedType = (() => {
          const rawType = (
            post.type ||
            post.post_type ||
            post.postType ||
            ""
          )
            .toString()
            .toLowerCase();

          if (["question", "poll", "story"].includes(rawType)) {
            return rawType;
          }

          if (post.is_question) {
            return "question";
          }

          const hasPollOptions =
            (Array.isArray(post.pollOptions) && post.pollOptions.length > 0) ||
            (Array.isArray(post.poll_options) && post.poll_options.length > 0);
          if (hasPollOptions) {
            return "poll";
          }

          if (post.is_story || post.story_id) {
            return "story";
          }

          return null;
        })();

        // Post type metadata (similar to ChannelPost)
        const postTypeMeta = (() => {
          switch (normalizedType) {
            case "question":
              return {
                key: "question",
                label: t("channelPost.types.question", "Question"),
                icon: (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                    <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path>
                    <circle cx="12" cy="17" r="1"></circle>
                  </svg>
                ),
              };
            case "poll":
              return {
                key: "poll",
                label: t("channelPost.types.poll", "Poll"),
                icon: (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="20" x2="18" y2="10"></line>
                    <line x1="12" y1="20" x2="12" y2="4"></line>
                    <line x1="6" y1="20" x2="6" y2="14"></line>
                  </svg>
                ),
              };
            case "story":
              return {
                key: "story",
                label: t("channelPost.types.story", "Story"),
                icon: (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                    <polyline points="14 2 14 8 20 8"></polyline>
                  </svg>
                ),
              };
            default:
              return null;
          }
        })();

        return (
          <div
            style={{
              marginBottom: "15px",
            }}
            key={post.id}
            className={`${styles["post-feed-post-container"]} ${postTypeMeta ? `channel-post channel-post--${postTypeMeta.key}` : ""}`}
          >
            {/* Post Header */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: "12px",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", flex: 1 }}>
                <img
                  src={primaryAvatar}
                  alt={primaryName}
                  style={{
                    width: "52px",
                    height: "52px",
                    borderRadius: "50%",
                    objectFit: "contain",
                    marginRight: "14px",
                    border: `3px solid ${socialUtils.getUserTypeColor(author?.userType)}`,
                    boxShadow: `0 0 0 1px rgba(255, 255, 255, 0.8)`,
                  }}
                  onError={(e) => {
                    e.target.src = getUserPlaceholderImage(author);
                  }}
                />

                <div style={{ flex: 1 }}>
                  <h5
                    style={{
                      margin: 0,
                      fontSize: "16px",
                      fontWeight: 400,
                      color: "#303E4E",
                      cursor: "pointer",
                      textDecoration: "underline",
                      display: "inline-block",
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (isChannelPost && post.channel?.id) {
                        window.location.href = `/social/channels/${post.channel.id}`;
                      } else {
                        // Only navigate if not current user
                        if (
                          author?.id &&
                          (currentUser.id === author.id ||
                            currentUser.username === author.username)
                        ) {
                          // Navigate to own profile
                          window.location.href = "/social/profile";
                        } else {
                          window.location.href = `/social/profile/${author?.id || author?.username || ""}`;
                        }
                      }
                    }}
                    title={
                      isChannelPost
                        ? t("postFeed.viewChannel", "View channel")
                        : t("postFeed.viewProfile")
                    }
                  >
                    {primaryName}
                  </h5>
                  {(post.reposted_post || post.original_post || post.is_repost || post.repost_id || post.type === 'repost') && (
                    <span
                      style={{
                        marginLeft: '8px',
                        fontSize: '14px',
                        color: '#6B7280',
                        fontWeight: 400,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                        pointerEvents: 'none',
                        userSelect: 'none'
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M17 1l4 4-4 4" />
                        <path d="M3 11V9a4 4 0 0 1 4-4h14" />
                        <path d="M7 23l-4-4 4-4" />
                        <path d="M21 13v2a4 4 0 0 1-4 4H3" />
                      </svg>
                      {t("postFeed.repost.reposted", "reposted")}
                    </span>
                  )}
                  <span
                    style={{
                      fontSize: "13px",
                      color: "#777",
                      fontWeight: 400,
                      padding: "0 0 0 10px",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                  >
                    {author?.isSponsored ? t("postFeed.sponsored") : post.timestamp}

                    {/* Processing Status */}
                    {post.status === 'processing' && (
                      <span
                        style={{
                          fontSize: "13px",
                          color: "#EAB308",
                          fontWeight: 600,
                          marginLeft: "8px",
                          display: "flex",
                          alignItems: "center",
                          gap: "4px"
                        }}
                      >
                        <div
                          style={{
                            width: "12px",
                            height: "12px",
                            border: "2px solid #EAB308",
                            borderTop: "2px solid transparent",
                            borderRadius: "50%",
                            animation: "spin 1s linear infinite",
                          }}
                        ></div>
                        {t("postFeed.processingVideo", "Processing Video...")}
                      </span>
                    )}

                    {/* Show "Edited" if post has been edited (using 1 minute threshold to avoid showing for background video optimization) */}
                    {post.updated_at && post.created_at &&
                      (new Date(post.updated_at) - new Date(post.created_at) > 60000) && (
                        <span
                          style={{
                            fontSize: "13px",
                            color: "#999",
                            fontStyle: "italic",
                            fontWeight: 400,
                          }}
                        >
                          {t("postFeed.edited")}
                        </span>
                      )}
                  </span>

                  {isChannelPost && (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        marginTop: "4px",
                        fontSize: "13px",
                        color: "#6B7280",
                        gap: "6px",
                      }}
                    >
                      <img
                        src={author?.avatar || getUserPlaceholderImage(author)}
                        alt={author?.username}
                        style={{
                          width: "18px",
                          height: "18px",
                          borderRadius: "50%",
                          objectFit: "cover",
                          border: "1px solid rgba(0,0,0,0.05)",
                        }}
                        onError={(e) => {
                          e.target.src = getUserPlaceholderImage(author);
                        }}
                      />
                      <span>
                        {t("postFeed.postedBy", "Posted by")} {author?.username}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              <div
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                {/* Post Type Badge */}
                {postTypeMeta && (
                  <span className={`channel-post-type-badge channel-post-type-badge--${postTypeMeta.key}`}>
                    {postTypeMeta.icon}
                    <span>{postTypeMeta.label}</span>
                  </span>
                )}
                <div style={{ position: "relative" }}>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleDropdown(post.id);
                    }}
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      padding: "6px",
                      borderRadius: "4px",
                    }}
                  >
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="#494949"
                    >
                      <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" />
                    </svg>

                    {/* Dropdown Menu */}
                    {showDropdowns[post.id] && (
                      <div
                        style={{
                          position: "absolute",
                          top: "100%",
                          right: 0,
                          background: "#FFFFFF",
                          borderRadius: "8px",
                          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                          padding: "8px 0",
                          minWidth: "120px",
                          zIndex: 1000,
                          border: "1px solid #E5E7EB",
                        }}
                      >
                        {isOwner ? (
                          <>
                            <div
                              onClick={() => startEditPost(post)}
                              style={{
                                padding: "8px 16px",
                                cursor: "pointer",
                                fontSize: "14px",
                                color: "#303E4E",
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                              }}
                              onMouseEnter={(e) =>
                                (e.target.style.background = "#F3F4F6")
                              }
                              onMouseLeave={(e) =>
                                (e.target.style.background = "transparent")
                              }
                            >
                              {getActionIcon("edit", 14)}
                              Edit
                            </div>
                            <div
                              onClick={() =>
                                onDeletePost && onDeletePost(post)
                              }
                              style={{
                                padding: "8px 16px",
                                cursor: "pointer",
                                fontSize: "14px",
                                color: "#EF4444",
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                              }}
                              onMouseEnter={(e) =>
                                (e.target.style.background = "#FEF2F2")
                              }
                              onMouseLeave={(e) =>
                                (e.target.style.background = "transparent")
                              }
                            >
                              {getActionIcon("delete", 14)}
                              Delete
                            </div>
                          </>
                        ) : (
                          <div
                            onClick={(e) => {
                              e.stopPropagation();
                              if (post.has_reported) {
                                return;
                              }
                              setSelectedPostForReport(post);
                              setShowReportModal(true);
                              toggleDropdown(post.id);
                            }}
                            style={{
                              padding: "8px 16px",
                              cursor: post.has_reported ? "not-allowed" : "pointer",
                              fontSize: "14px",
                              color: post.has_reported ? "#9CA3AF" : "#6B7280",
                              opacity: post.has_reported ? 0.6 : 1,
                            }}
                            onMouseEnter={(e) => {
                              if (!post.has_reported) {
                                e.target.style.background = "#F3F4F6";
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (!post.has_reported) {
                                e.target.style.background = "transparent";
                              }
                            }}
                          >
                            {post.has_reported ? (t("postFeed.alreadyReported") || "Already Reported") : t("postFeed.report")}
                          </div>
                        )}
                      </div>
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* Post Content */}
            <>
              {/* Repost Display */}
              {(() => {
                // Check if this is a repost AND we have the original post data
                // Only render repost UI if we have the nested original post data
                const originalPost = post.reposted_post || post.original_post;
                // Use same repost detection as media section for consistency
                const isRepost = post.is_repost ||
                  post.repost_id ||
                  post.parent_post_id ||
                  post.type === 'repost' ||
                  (post.reposted_post && Object.keys(post.reposted_post).length > 0) ||
                  (post.original_post && Object.keys(post.original_post).length > 0);
                const hasOriginalPostData = originalPost && typeof originalPost === 'object' && Object.keys(originalPost).length > 0;

                // If it's a repost but missing original post data, still mark it as repost to prevent media rendering
                // Only render repost UI if we have both the repost flag AND the original post data
                if (!isRepost || !hasOriginalPostData) {
                  // If it's a repost but missing data, return a marker to prevent regular post rendering
                  if (isRepost) {
                    return <div style={{ display: 'none' }} data-is-repost="true" />;
                  }
                  return null; // Not a repost - will fall through to regular post rendering
                }

                // For reposts, post.content is the reposter's comment (per backend implementation)
                // Also check repost_response_text as alternative field name
                const repostResponse = post.repost_response_text || post.response_text || post.repost_comment || (post.is_repost ? post.content : null);
                const reposter = post.author || post.user;

                const displayOriginalPost = originalPost;

                // Get the author from displayOriginalPost (which should have the transformed author from transformPostData)
                // Fallback to originalPost.user if displayOriginalPost doesn't have author
                const originalAuthor = displayOriginalPost?.author ||
                  (displayOriginalPost?.user ? {
                    id: displayOriginalPost.user.id || 0,
                    name: displayOriginalPost.user.first_name && displayOriginalPost.user.last_name
                      ? `${displayOriginalPost.user.first_name} ${displayOriginalPost.user.last_name}`
                      : displayOriginalPost.user.username || 'Unknown',
                    username: displayOriginalPost.user.username,
                    avatar: displayOriginalPost.user.image || displayOriginalPost.user.profile_image_url || displayOriginalPost.user.profile_image || getUserPlaceholderImage(displayOriginalPost.user),
                    userType: displayOriginalPost.user.user_type || 'PET_GUARDIAN',
                  } : {
                    id: 0,
                    name: 'Unknown',
                    username: 'unknown',
                    avatar: getUserPlaceholderImage(null),
                    userType: 'PET_GUARDIAN',
                  });

                // Get the original post ID once for use in click handler
                const originalPostId = displayOriginalPost?.id ||
                  originalPost?.id ||
                  post.repost_id ||
                  post.parent_post_id ||
                  (displayOriginalPost?.reposted_post?.id) ||
                  (originalPost?.reposted_post?.id);

                return (
                  <div style={{ marginBottom: "15px" }}>
                    {/* Reposter's Response/Comment */}
                    {repostResponse && (
                      <div
                        className={styles["repost-comment"]}
                        dir={isArabicText(repostResponse) ? "rtl" : "ltr"}
                        style={{
                          textAlign: isArabicText(repostResponse) ? "right" : "left",
                        }}
                      >
                        {formatContent(repostResponse)}
                      </div>
                    )}

                    {/* Original Post */}
                    <div
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        if (originalPostId) {
                          openPostModal(String(originalPostId));
                        }
                      }}
                      onMouseDown={(e) => {
                        // Also handle mousedown to ensure click works
                        e.stopPropagation();
                      }}
                      style={{
                        border: "1px solid #E5E7EB",
                        borderRadius: "12px",
                        padding: "16px",
                        backgroundColor: "#FFFFFF",
                        maxWidth: "95%",
                        marginLeft: "auto",
                        marginRight: "auto",
                        marginTop: repostResponse ? "12px" : "0",
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                        position: "relative",
                        zIndex: 100,
                        pointerEvents: "auto",
                        isolation: "isolate",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = "#9CA3AF";
                        e.currentTarget.style.boxShadow = "0 2px 8px rgba(0, 0, 0, 0.1)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = "#E5E7EB";
                        e.currentTarget.style.boxShadow = "none";
                      }}
                    >

                      {/* Original Post Header */}
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "12px",
                          marginBottom: "12px",
                        }}
                      >
                        <img
                          src={
                            originalAuthor?.avatar ||
                            originalAuthor?.image ||
                            originalAuthor?.profile_image_url ||
                            originalAuthor?.profile_image ||
                            getUserPlaceholderImage(originalAuthor)
                          }
                          alt={originalAuthor?.name || "Author"}
                          style={{
                            width: "40px",
                            height: "40px",
                            borderRadius: "50%",
                            objectFit: "cover",
                            border: `2px solid ${socialUtils.getUserTypeColor(originalAuthor?.userType)}`,
                          }}
                          onError={(e) => {
                            e.target.src = getUserPlaceholderImage(originalAuthor);
                          }}
                        />
                        <div style={{ flex: 1 }}>
                          <div
                            style={{
                              fontSize: "14px",
                              fontWeight: 500,
                              color: "#111827",
                            }}
                          >
                            {originalAuthor?.first_name && originalAuthor?.last_name
                              ? `${originalAuthor.first_name} ${originalAuthor.last_name}`
                              : originalAuthor?.name || originalAuthor?.username || "Unknown"}
                          </div>
                          <div
                            style={{
                              fontSize: "12px",
                              color: "#6B7280",
                            }}
                          >
                            {displayOriginalPost.timestamp || displayOriginalPost.created_at || "Recently"}
                          </div>
                        </div>
                      </div>

                      {/* Original Post Content */}
                      {displayOriginalPost.content && (
                        <div
                          style={{
                            fontSize: "14px",
                            color: "#374151",
                            lineHeight: "1.5",
                            marginBottom: "8px",
                          }}
                          dir={isArabicText(displayOriginalPost.content) ? "rtl" : "ltr"}
                        >
                          {formatContent(displayOriginalPost.content)}
                        </div>
                      )}

                      {/* Original Post Media */}
                      {/* Only render media array - image field is legacy and should be in media array */}
                      {displayOriginalPost?.media && Array.isArray(displayOriginalPost.media) && displayOriginalPost.media.length > 0 ? (
                        <div style={{ marginTop: "8px" }}>
                          <MultiMediaDisplay
                            media={displayOriginalPost.media}
                            postId={displayOriginalPost?.id || post.id}
                          />
                        </div>
                      ) : displayOriginalPost?.image ? (
                        // Fallback: if media array is empty but image field exists, show it
                        <img
                          src={displayOriginalPost?.image}
                          alt="Post content"
                          style={{
                            width: "100%",
                            maxHeight: "300px",
                            objectFit: "cover",
                            borderRadius: "8px",
                            marginTop: "8px",
                          }}
                        />
                      ) : null}

                      {/* Original Adoption Post Embed */}
                      {(() => {
                        const adoptionPostId = displayOriginalPost.adoption_post_id ||
                          displayOriginalPost.adoption_post?.id ||
                          displayOriginalPost.metadata?.adoption_post_id ||
                          displayOriginalPost.metadata?.adoption_post?.id ||
                          (displayOriginalPost.post_type === 'adoption' ? displayOriginalPost.id : null);

                        const adoptionPostData = displayOriginalPost.adoption_post ||
                          displayOriginalPost.metadata?.adoption_post ||
                          null;

                        if (!adoptionPostId) return null;

                        return (
                          <div style={{ marginTop: "16px" }} onClick={(e) => e.stopPropagation()}>
                            <AdoptionPostEmbed
                              adoptionPostId={Number(adoptionPostId)}
                              adoptionPost={adoptionPostData}
                              currentUser={currentUser}
                              onAdoptionRequestSent={(id) => {
                                // Update local state for the original post if needed
                              }}
                            />
                          </div>
                        );
                      })()}

                      {/* Original Post Counters */}
                      {(() => {
                        // Get counts from various possible locations
                        // First try from the transformed displayOriginalPost, then originalPost, then from the raw post data
                        // Use nullish coalescing (??) to preserve 0 values, only fallback if undefined/null
                        const rawRepostedPost = post.reposted_post || post.original_post;
                        const reactionsCount = displayOriginalPost.reactions_count ??
                          displayOriginalPost.reactions?.likes ??
                          originalPost.reactions_count ??
                          originalPost.reactions?.likes ??
                          rawRepostedPost?.reactions_count ??
                          rawRepostedPost?.reactions?.likes ??
                          0;
                        const commentsCount = displayOriginalPost.comments_count ??
                          displayOriginalPost.comments ??
                          displayOriginalPost.reactions?.comments ??
                          originalPost.comments_count ??
                          originalPost.comments ??
                          originalPost.reactions?.comments ??
                          rawRepostedPost?.comments_count ??
                          rawRepostedPost?.comments ??
                          rawRepostedPost?.reactions?.comments ??
                          0;
                        const repostsCount = displayOriginalPost.reposts_count ??
                          originalPost.reposts_count ??
                          rawRepostedPost?.reposts_count ??
                          0;
                        const sharesCount = displayOriginalPost.shares_count ??
                          displayOriginalPost.shares ??
                          displayOriginalPost.reactions?.shares ??
                          originalPost.shares_count ??
                          originalPost.shares ??
                          originalPost.reactions?.shares ??
                          rawRepostedPost?.shares_count ??
                          rawRepostedPost?.shares ??
                          rawRepostedPost?.reactions?.shares ??
                          0;

                        // Always show counters section (even if all are 0, show 0)
                        return (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "16px",
                              marginTop: "12px",
                              paddingTop: "12px",
                              borderTop: "1px solid #E5E7EB",
                              fontSize: "13px",
                              color: "#6B7280",
                            }}
                          >
                            {/* Reactions Count */}
                            <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
                              </svg>
                              {reactionsCount}
                            </span>

                            {/* Comments Count */}
                            <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                              </svg>
                              {commentsCount}
                            </span>

                            {/* Reposts Count */}
                            <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M17 1l4 4-4 4" />
                                <path d="M3 11V9a4 4 0 0 1 4-4h14" />
                                <path d="M7 23l-4-4 4-4" />
                                <path d="M21 13v2a4 4 0 0 1-4 4H3" />
                              </svg>
                              {repostsCount}
                            </span>

                            {/* Shares Count */}
                            <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="18" cy="5" r="3"></circle>
                                <circle cx="6" cy="12" r="3"></circle>
                                <circle cx="18" cy="19" r="3"></circle>
                                <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"></line>
                                <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"></line>
                              </svg>
                              {sharesCount}
                            </span>
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                );
              })()}

              {/* Question Post */}
              {normalizedType === "question" && !(post.reposted_post && Object.keys(post.reposted_post).length > 0) && !(post.original_post && Object.keys(post.original_post).length > 0) && (
                <div className="channel-post-content channel-post-question" style={{ margin: "15px 0 21px 0" }}>
                  <div className="channel-post-question-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10"></circle>
                      <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path>
                      <circle cx="12" cy="17" r="1"></circle>
                    </svg>
                  </div>
                  <div
                    className="channel-post-text"
                    dir={isArabicText(post.content) ? "rtl" : "ltr"}
                    style={{
                      fontSize: "18px",
                      fontWeight: "400",
                      color: "#494949",
                      lineHeight: "1.5",
                      margin: 0,
                    }}
                  >
                    {formatContent(displayContent)}
                  </div>
                </div>
              )}

              {/* Poll Post */}
              {normalizedType === "poll" && !(post.reposted_post && Object.keys(post.reposted_post).length > 0) && !(post.original_post && Object.keys(post.original_post).length > 0) && (() => {
                const pollOptions = Array.isArray(post.pollOptions)
                  ? post.pollOptions.map(option => ({
                    id: option.id || option.option_id || null,
                    text: option.text || option.option || option.label || option,
                    votes: option.votes || option.votes_count || 0,
                    votes_count: option.votes || option.votes_count || 0,
                    option_id: option.id || option.option_id || null,
                  }))
                  : Array.isArray(post.poll_options)
                    ? post.poll_options.map(option => ({
                      id: option.id || option.option_id || null,
                      text: option.text || option.option || option.label || option,
                      votes: option.votes || option.votes_count || 0,
                      votes_count: option.votes || option.votes_count || 0,
                      option_id: option.id || option.option_id || null,
                    }))
                    : [];

                const totalPollVotes = typeof post.poll_total_votes === "number"
                  ? post.poll_total_votes
                  : pollOptions.reduce(
                    (sum, option) => sum + (option.votes || option.votes_count || 0),
                    0
                  );

                const userVoteOptionId = post.poll_user_vote_option_id || post.poll_user_vote_id || userPollVotes[post.id] || null;
                const selectedOptionId = selectedPollOptions[post.id] !== undefined
                  ? selectedPollOptions[post.id]
                  : userVoteOptionId;
                const isSubmittingVote = isSubmittingPollVote[post.id] || false;

                const handleSelectPollOption = (optionId) => {
                  setSelectedPollOptions((prev) => ({ ...prev, [post.id]: optionId }));
                };

                const handleConfirmPollVote = async () => {
                  if (!selectedOptionId || selectedOptionId === userVoteOptionId || isSubmittingVote) {
                    return;
                  }

                  setIsSubmittingPollVote((prev) => ({ ...prev, [post.id]: true }));

                  try {
                    await postsAPI.voteOnPoll(post.id, selectedOptionId);

                    const updatedPollOptions = pollOptions.map((option) => {
                      const optionIdentifier = option.id || option.option_id;
                      const baseVotes = option.votes || option.votes_count || 0;

                      let updatedVotes = baseVotes;

                      if (userVoteOptionId === null) {
                        if (optionIdentifier === selectedOptionId) {
                          updatedVotes = baseVotes + 1;
                        }
                      } else if (userVoteOptionId !== selectedOptionId) {
                        if (optionIdentifier === selectedOptionId) {
                          updatedVotes = baseVotes + 1;
                        }
                        if (optionIdentifier === userVoteOptionId) {
                          updatedVotes = Math.max(baseVotes - 1, 0);
                        }
                      }

                      return {
                        ...option,
                        votes: updatedVotes,
                        votes_count: updatedVotes,
                      };
                    });

                    const nextTotalVotes = userVoteOptionId === null
                      ? totalPollVotes + 1
                      : userVoteOptionId === selectedOptionId
                        ? totalPollVotes
                        : totalPollVotes;

                    setUserPollVotes((prev) => ({ ...prev, [post.id]: selectedOptionId }));

                    stableOnUpdatePost(post.id, {
                      ...post,
                      pollOptions: updatedPollOptions,
                      poll_options: updatedPollOptions,
                      poll_total_votes: nextTotalVotes,
                      poll_user_vote_option_id: selectedOptionId,
                      poll_user_vote_id: selectedOptionId,
                    });
                  } catch (error) {
                    handleApiError(error);
                  } finally {
                    setIsSubmittingPollVote((prev) => ({ ...prev, [post.id]: false }));
                  }
                };

                return (
                  <div className="channel-post-content channel-post-poll" style={{ margin: "15px 0 21px 0" }}>
                    {post.content && (
                      <div
                        className="channel-post-text"
                        dir={isArabicText(post.content) ? "rtl" : "ltr"}
                        style={{
                          fontSize: "18px",
                          fontWeight: "400",
                          color: "#494949",
                          lineHeight: "1.5",
                          marginBottom: "16px",
                        }}
                      >
                        {formatContent(displayContent)}
                      </div>
                    )}
                    {pollOptions.length > 0 && (
                      <div className="channel-post-poll-options" onClick={(e) => e.stopPropagation()}>
                        {pollOptions.map((option, index) => {
                          const voteCount = option.votes || option.votes_count || 0;
                          const percentage =
                            totalPollVotes > 0 ? (voteCount / totalPollVotes) * 100 : 0;
                          const optionId = option.id || option.option_id || index;
                          const isCurrentVote = userVoteOptionId === optionId;
                          const isSelected = selectedOptionId === optionId;
                          const isPendingSelection =
                            isSelected && selectedOptionId !== userVoteOptionId;
                          const optionClasses = [
                            "channel-post-poll-option",
                            isSelected ? "selected" : "",
                            isCurrentVote ? "voted" : "",
                            isPendingSelection ? "pending" : "",
                            userVoteOptionId ? "active-results" : "",
                          ]
                            .filter(Boolean)
                            .join(" ");

                          return (
                            <button
                              type="button"
                              key={optionId}
                              className={optionClasses}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSelectPollOption(optionId);
                              }}
                              disabled={isSubmittingVote}
                            >
                              <div className="poll-option-container">
                                <div
                                  className="poll-option-fill"
                                  style={{ width: `${percentage}%` }}
                                ></div>
                                <div className="poll-option-content">
                                  <span className="poll-option-text">
                                    {option.text || option.option || option.label || option}
                                  </span>
                                  <div className="poll-option-stats">
                                    <span className="poll-option-percentage">
                                      {Math.round(percentage)}%
                                    </span>
                                    {isCurrentVote && (
                                      <span className="poll-option-voted-badge">
                                        {t("channelPost.yourVote", "Your vote")}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                              {isSelected && !userVoteOptionId && (
                                <div className="poll-option-checkmark">
                                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                                    <polyline points="20 6 9 17 4 12" />
                                  </svg>
                                </div>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    )}
                    <div className="channel-post-poll-footer" onClick={(e) => e.stopPropagation()}>
                      <div className="channel-post-poll-totals">
                        {t("channelPost.pollTotalVotes", "{{count}} total votes", {
                          count: totalPollVotes,
                        })}
                      </div>
                      <button
                        type="button"
                        className="channel-post-poll-submit"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleConfirmPollVote();
                        }}
                        disabled={
                          isSubmittingVote ||
                          !selectedOptionId ||
                          selectedOptionId === userVoteOptionId
                        }
                      >
                        {isSubmittingVote
                          ? t("channelPost.pollSubmitting", "Submitting...")
                          : userVoteOptionId
                            ? t("channelPost.pollUpdateVote", "Update vote")
                            : t("channelPost.pollSubmitVote", "Submit vote")}
                      </button>
                    </div>
                    {post.poll_expires_at && (
                      <div className="channel-post-poll-meta">
                        {t("channelPost.pollExpires", "Poll closes {{date}}", {
                          date: new Date(post.poll_expires_at).toLocaleString(),
                        })}
                      </div>
                    )}
                  </div>
                );
              })()}


              {/* Regular Post Content - Only render if NOT a repost */}
              {(() => {
                // Check if this is a repost using same detection as other sections
                const isRepost = post.is_repost ||
                  post.repost_id ||
                  post.parent_post_id ||
                  post.type === 'repost' ||
                  (post.reposted_post && Object.keys(post.reposted_post).length > 0) ||
                  (post.original_post && Object.keys(post.original_post).length > 0);

                // Don't render regular post content if it's a repost
                if (isRepost) {
                  return null;
                }

                // Check if this post is sharing an adoption post
                const adoptionPostId = post.adoption_post_id ||
                  post.adoption_post?.id ||
                  post.metadata?.adoption_post_id ||
                  post.metadata?.adoption_post?.id;

                // Render content section if there's content
                const hasContent = displayContent && displayContent.trim().length > 0;
                const shouldRenderContent = normalizedType !== "question" && normalizedType !== "poll" && hasContent;

                if (!shouldRenderContent && !adoptionPostId) {
                  return null;
                }

                return (
                  <>
                    {/* Post Text Content */}
                    {hasContent && (
                      <div
                        style={{
                          margin: "15px 0 21px 0",
                          fontSize: "18px",
                          fontWeight: "400",
                          color: "#494949",
                          lineHeight: "1.5",
                          direction: isArabicText(post.content) ? "rtl" : "ltr",
                          textAlign: isArabicText(post.content) ? "right" : "left",
                        }}
                      >
                        {formatContent(displayContent)}

                        {shouldShowExpansion && (
                          <button
                            onClick={() => togglePostExpansion(post.id)}
                            style={{
                              background: "none",
                              border: "none",
                              color: "#2a8ad2",
                              cursor: "pointer",
                              fontSize: "19px",
                              padding: 0,
                              marginTop: "8px",
                              textDecoration: "none",
                            }}
                            onMouseEnter={(e) =>
                              (e.target.style.textDecoration = "underline")
                            }
                            onMouseLeave={(e) =>
                              (e.target.style.textDecoration = "none")
                            }
                          >
                            {isExpanded
                              ? t("postFeed.showLess")
                              : t("postFeed.showMore")}
                          </button>
                        )}
                      </div>
                    )}

                    {/* Adoption Post Embed - Renders separately so it always shows when present */}
                    {adoptionPostId && (
                      <div style={{ marginTop: hasContent ? "16px" : "15px", marginBottom: "21px" }}>
                        <AdoptionPostEmbed
                          adoptionPostId={Number(adoptionPostId)}
                          adoptionPost={post.adoption_post || null}
                          currentUser={currentUser}
                          onAdoptionRequestSent={(id) => {
                            // Optionally refresh the post or update state
                            if (onUpdatePost) {
                              onUpdatePost(post.id, {
                                ...post,
                                adoption_post: {
                                  ...post.adoption_post,
                                  my_request_status: "pending",
                                },
                              });
                            }
                          }}
                        />
                      </div>
                    )}
                  </>
                );
              })()}
            </>

            {/* Post Media Display - Using MultiMediaDisplay for 2-per-row grid */}
            {/* IMPORTANT: For reposts, NEVER show post.media - it should be empty from backend
                but add explicit check to prevent any media from showing for reposts */}
            {(() => {
              // Check if this is a repost using multiple indicators
              const isRepost = post.is_repost ||
                post.repost_id ||
                post.parent_post_id ||
                post.type === 'repost' ||
                (post.reposted_post && Object.keys(post.reposted_post).length > 0) ||
                (post.original_post && Object.keys(post.original_post).length > 0);

              // For reposts: Do NOT render post.media at all
              // Original post media should only appear in the nested reposted_post.media section
              if (isRepost) {
                return null;
              }

              // For regular posts: show media normally
              if (post.media && Array.isArray(post.media) && post.media.length > 0) {
                return (
                  <div style={{ marginTop: "12px" }}>
                    <MultiMediaDisplay
                      media={post.media.map(item => ({
                        url: item.url || item,
                        type: item.type || (typeof item === 'string' ?
                          (item.includes('.mp4') || item.includes('.mov') || item.includes('.webm') ? 'video' : 'image')
                          : 'image'),
                        file_type: item.type || item.file_type,
                        resolutions: item.resolutions || null,
                        thumbnail: item.thumbnail || null
                      }))}
                    />
                  </div>
                );
              }
              return null;
            })()}

            {/* Post Footer - Using Channel Design */}
            <div className="channel-post-footer" style={{ marginTop: "16px" }} onClick={(e) => e.stopPropagation()}>
              <ChannelPostReactions
                post={{
                  ...post,
                  // Ensure ChannelPostReactions has the fields it expects
                  likes: post.reactions?.likes || post.likes || 0,
                  shares: post.reactions?.shares || post.shares || 0,
                  comments: post.reactions?.comments || post.comments || 0,
                  reposts_count: post.reposts_count || 0,
                  user_has_reposted: post.user_has_reposted || false,
                  user_reaction_type: post.user_reaction_type || null,
                  reaction_counts: post.reaction_counts || {},
                  reactions_count: post.reactions_count || post.reactions?.likes || post.likes || 0,
                }}
                onLike={(updatedPost) => {
                  // Direct update like ChannelPost - simpler and faster
                  stableOnUpdatePost(post.id, updatedPost);
                }}
                onRepost={(updatedPost) => {
                  // Update post with repost count
                  stableOnUpdatePost(post.id, updatedPost);
                }}
                onNewRepost={onNewRepost}
                onShare={async () => {
                  try {
                    const response = await sharesAPI.sharePost(post.id);
                    const responseData = response?.data?.data || response?.data || {};

                    // Get share URL from backend response (preferred) or fallback to constructing it
                    const shareUrl = responseData?.share_url || responseData?.url ||
                      `${window.location.origin}/social/profile/posts/${post.id}`;

                    // Copy URL to clipboard
                    const didCopy = await copyTextToClipboard(shareUrl);
                    if (didCopy) {
                      showSuccessToast(t("postFeed.shareActions.copySuccess") || "Link copied to clipboard!");
                    } else {
                      showErrorToast(t("postFeed.shareActions.copyError") || "Failed to copy link");
                      window.prompt(t("postFeed.shareActions.manualCopyPrompt") || "Copy this link:", shareUrl);
                    }

                    // If backend indicates already shared, do not increment locally
                    if (responseData?.already_shared === true) {
                      return;
                    }

                    const currentPost = posts.find(p => p.id === post.id);
                    if (currentPost && stableOnUpdatePost) {
                      const nextShares =
                        responseData?.shares_count ??
                        responseData?.shares ??
                        ((currentPost.reactions?.shares || currentPost.shares || 0) + 1);

                      // Update share_url in post object
                      const updatedPost = {
                        ...currentPost,
                        share_url: shareUrl,
                        shares: nextShares,
                        reactions: {
                          ...currentPost.reactions,
                          shares: nextShares,
                        },
                      };
                      stableOnUpdatePost(post.id, updatedPost);
                    }
                  } catch (error) {
                    // If backend prevents duplicates (409), we should NOT increment locally.
                    const status = error?.response?.status;
                    if (status && Number(status) === 409) return;
                    console.error("Error sharing post:", error);
                  }
                }}
                onComment={() => {
                  toggleComments(post.id);
                }}
                onShowReactions={handleShowReactions}
              />
            </div>

            {/* Comments Section - Using Channel Design */}
            {showComments && (
              <ChannelPostComments
                post={post}
                onCommentAdded={(comment) => {
                  // Update comment count
                  if (onUpdatePost) {
                    onUpdatePost(post.id, {
                      comments: (post.reactions?.comments || post.comments || 0) + 1,
                      reactions: {
                        ...post.reactions,
                        comments: (post.reactions?.comments || post.comments || 0) + 1,
                      },
                    });
                  }
                  // Trigger parent callback if provided
                  if (onCommentAdded) {
                    onCommentAdded(post.id);
                  }
                }}
              />
            )}
          </div>
        );
      })}

      {/* Load More Button */}
      {hasMorePosts && (
        <div style={{ textAlign: "center", margin: "20px 0" }}>
          <button
            onClick={onLoadMore}
            disabled={isLoading}
            style={{
              background: isLoading ? "#ccc" : "#8548A8",
              color: "white",
              border: "none",
              borderRadius: "25px",
              padding: "12px 24px",
              fontSize: "16px",
              fontWeight: 500,
              cursor: isLoading ? "not-allowed" : "pointer",
              transition: "background 0.3s ease",
              fontFamily: '"Red Hat Display", sans-serif',
            }}
            onMouseEnter={(e) => {
              if (!isLoading) {
                e.target.style.background = "#6B3F96";
              }
            }}
            onMouseLeave={(e) => {
              if (!isLoading) {
                e.target.style.background = "#8548A8";
              }
            }}
          >
            {isLoading ? (
              <div
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                <div
                  style={{
                    width: "16px",
                    height: "16px",
                    border: "2px solid #ffffff",
                    borderTop: "2px solid transparent",
                    borderRadius: "50%",
                    animation: "spin 1s linear infinite",
                  }}
                ></div>
                {t("postFeed.loading")}
              </div>
            ) : (
              t("postFeed.loadMore")
            )}
          </button>
        </div>
      )}

      {/* Post Reactions Modal */}
      <PostReactionsModal
        isOpen={showReactionsModal}
        onClose={handleCloseReactionsModal}
        postId={selectedPostId}
        currentUser={currentUser}
      />

      {/* Report Modal */}
      <ReportModal
        isOpen={showReportModal}
        onClose={() => {
          setShowReportModal(false);
          setSelectedPostForReport(null);
        }}
        reportableType={selectedPostForReport ? "App\\Models\\Post" : null}
        reportableId={selectedPostForReport?.id || null}
        post={selectedPostForReport || null}
        onSuccess={() => {
          // Optionally refresh posts or show success message
        }}
      />

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        .post-feed-post-container {
          transition: all 0.3s ease-in-out;
        }
        
        .comments-section {
          transition: all 0.3s ease-in-out;
          transform-origin: top;
        }
      `}</style>
    </div>
  );
};

export default PostFeed;
